import type {
  AmplifyKeys,
  OfflineSnapshot,
  QuickLoginUnlockResult,
} from "@observe-tech/utils";
import type { AuthApi } from "./auth";
import {
  amplifyClientId,
  captureAmplifyKeys,
  clearAmplifyKeysLocally,
  restoreAmplifyKeys,
} from "./amplifyKeys";
import {
  forgetQuickLogin,
  isQuickLoginCapable,
  listQuickLogins,
  quickLoginAvailable,
  rememberQuickLogin,
  setQuickLoginPin,
  unlockQuickLogin,
} from "./quickLoginSession";
import { clearSyncedTokens } from "./syncedTokens";
import { isAuthError } from "./offlineBoot";
import {
  clearSnapshot,
  isSnapshotUsable,
  readSnapshot,
} from "./offlineSession";
import {
  clearEmergencyMarker,
  isEmergencySession,
} from "../exported/shared/utils/emergencySession";

export const QUICK_LOGIN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface QuickLoginDeps {
  auth: Pick<AuthApi, "getToken" | "getAuthTimeMs" | "loadCurrentUser">;
  probeOnline(): Promise<boolean>;
  offlineModeEnabled(): boolean;
  now?(): number;
}

export type PinLoginOutcome =
  | Exclude<QuickLoginUnlockResult, { status: "ok" }>
  | { status: "online" }
  | { status: "offline"; snapshot: OfflineSnapshot }
  | { status: "noSnapshot" }
  | { status: "refused" };

export type QuickLoginSuccess = Extract<
  PinLoginOutcome,
  { status: "online" | "offline" }
>;

interface LiveSession {
  sub: string;
  email: string;
  keys: AmplifyKeys;
  expiresAt: number;
}

async function captureLiveSession(
  deps: Pick<QuickLoginDeps, "auth">,
): Promise<LiveSession | null> {
  if (isEmergencySession() || !isQuickLoginCapable()) return null;
  const authTime = await deps.auth.getAuthTimeMs();
  if (authTime === null) return null;
  const keys = captureAmplifyKeys(amplifyClientId());
  if (!keys) return null;
  const { cognitoSub, email } = await deps.auth.loadCurrentUser();
  return {
    sub: cognitoSub,
    email,
    keys,
    expiresAt: authTime + QUICK_LOGIN_TTL_MS,
  };
}

export async function rememberAfterPasswordLogin(
  deps: Pick<QuickLoginDeps, "auth">,
): Promise<boolean> {
  try {
    const session = await captureLiveSession(deps);
    if (!session) return false;
    if (!(await quickLoginAvailable())) return false;
    const { sub, email, keys, expiresAt } = session;
    const row = (await listQuickLogins()).find((r) => r.cognitoSub === sub);
    if (!row) return true;
    const saved = await rememberQuickLogin(sub, email, keys, expiresAt);
    if (!saved) return false;
    return !row.hasPin;
  } catch (error) {
    console.warn("[quickLogin] remember failed", error);
    return false;
  }
}

export async function enrolPin(
  deps: Pick<QuickLoginDeps, "auth">,
  pin: string,
): Promise<boolean> {
  const session = await captureLiveSession(deps);
  if (!session) return false;
  const { sub, email, keys, expiresAt } = session;
  if (!(await rememberQuickLogin(sub, email, keys, expiresAt))) return false;
  return setQuickLoginPin(sub, pin, keys, expiresAt);
}

async function dropRestoredKeys(): Promise<void> {
  await clearAmplifyKeysLocally();
  clearSyncedTokens();
}

function isRefusal(error: unknown): boolean {
  return (
    isAuthError(error) ||
    (error instanceof Error && error.message === "No session")
  );
}

export async function pinLogin(
  deps: QuickLoginDeps,
  sub: string,
  pin: string,
): Promise<PinLoginOutcome> {
  const result = await unlockQuickLogin(sub, pin);
  if (result.status !== "ok") return result;

  await dropRestoredKeys();
  clearEmergencyMarker();
  restoreAmplifyKeys(result.keys);

  if (await deps.probeOnline()) {
    try {
      await deps.auth.getToken({ forceRefresh: true });
      return { status: "online" };
    } catch (error) {
      if (isRefusal(error)) {
        await dropRestoredKeys();
        await clearSnapshot(sub);
        return { status: "refused" };
      }
      console.warn("[quickLogin] online PIN refresh failed", error);
    }
  }

  const snapshot = deps.offlineModeEnabled() ? await readSnapshot(sub) : null;
  const now = deps.now?.() ?? Date.now();
  if (!snapshot || !isSnapshotUsable(snapshot, now)) {
    await dropRestoredKeys();
    return { status: "noSnapshot" };
  }
  return { status: "offline", snapshot };
}

export async function forgetAccount(sub: string): Promise<void> {
  await forgetQuickLogin(sub);
}
